import React, { useState, useEffect } from "react";
import AccordionCategory from "../AccordionCategory/AccordionCategory";
import styles from "./AcademicActivites.module.css";

/* ================= HELPERS ================= */

/* Preserve hierarchy EXACTLY */
const normalizeNode = (node) => ({
  id: node.id,
  name: node.name,
  children: (node.children || []).map(normalizeNode),
});

const formatAccordionData = (list = []) =>
  list.map((item) => ({
    categoryId: item.category_id,
    categoryName: item.category,
    subCategories: (item.items || []).map(normalizeNode),
  }));

/* Filter accordion data based on search query */
const filterAccordionData = (categories, searchQuery) => {
  if (!searchQuery.trim()) return categories;
  
  const query = searchQuery.toLowerCase();
  
  return categories
    .map(category => {
      // Filter subCategories that match the search query
      const filteredSubCategories = category.subCategories
        .map(subCategory => filterNode(subCategory, query))
        .filter(Boolean);
      
      return {
        ...category,
        subCategories: filteredSubCategories
      };
    })
    .filter(category => 
      category.categoryName.toLowerCase().includes(query) ||
      category.subCategories.length > 0
    );
};

/* Recursively filter nodes and their children */
const filterNode = (node, query) => {
  const nodeMatches = node.name.toLowerCase().includes(query);
  
  let filteredChildren = [];
  if (node.children && node.children.length > 0) {
    filteredChildren = node.children
      .map(child => filterNode(child, query))
      .filter(Boolean);
  }
  
  // Include node if it matches or has matching children
  if (nodeMatches || filteredChildren.length > 0) {
    return {
      ...node,
      children: filteredChildren
    };
  }
  
  return null;
};

/* ================= COMPONENT ================= */

const CategorySubCategory = ({
  register,
  setValue,
  subjectExpertiseList,
  mentorshipOfferedList,
  apiSubjectExpertise,
  apiMentorshipOffered,
  disabled,
}) => {
  const [subjectExpertise, setSubjectExpertise] = useState({});
  const [mentorshipOffered, setMentorshipOffered] = useState({});
  const [subjectSearchQuery, setSubjectSearchQuery] = useState("");
  const [mentorshipSearchQuery, setMentorshipSearchQuery] = useState("");
  useEffect(() => {
    if (apiSubjectExpertise && Object.keys(apiSubjectExpertise).length) {
      setSubjectExpertise(apiSubjectExpertise);
      setValue("subjectMatterExpertise", apiSubjectExpertise);
    }

    if (apiMentorshipOffered && Object.keys(apiMentorshipOffered).length) {
      setMentorshipOffered(apiMentorshipOffered);
      setValue("mentorshipOffered", apiMentorshipOffered);
    }
  }, [apiSubjectExpertise, apiMentorshipOffered, setValue]);

  /* ================= HANDLERS ================= */

  const handleSubjectSelection = (categoryId, tree) => {
    const updated = {
      ...subjectExpertise,
      [categoryId]: tree,
    };

    setSubjectExpertise(updated);
    setValue("subjectMatterExpertise", updated, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  const handleMentorshipSelection = (categoryId, tree) => {
    const updated = {
      ...mentorshipOffered,
      [categoryId]: tree,
    };

    setMentorshipOffered(updated);
    setValue("mentorshipOffered", updated, {
      shouldDirty: true,
      shouldTouch: true,
    });
  };

  /* ================= DATA ================= */

  const subjectCategories = formatAccordionData(subjectExpertiseList);
  const mentorshipCategories = formatAccordionData(mentorshipOfferedList);
  
  const filteredSubjectCategories = filterAccordionData(subjectCategories, subjectSearchQuery);
  const filteredMentorshipCategories = filterAccordionData(mentorshipCategories, mentorshipSearchQuery);

  /* ================= RENDER ================= */

  return (
    <div className={`${styles.grid} ${styles.marginBottom}`}>
      {/* ================= SUBJECT ================= */}
      <div>
        <h2>
          <span className={styles.blue}>Subject</span>{" "}
          <span className={styles.orange}>Matter Expertise</span>
        </h2>
        
        <input
          type="text"
          placeholder="Search subject expertise..."
          value={subjectSearchQuery}
          onChange={(e) => setSubjectSearchQuery(e.target.value)}
          className={styles.searchInput}
          disabled={disabled}
        />

        {filteredSubjectCategories.map((item) => (
          <AccordionCategory
            key={item.categoryId}
            categoryId={item.categoryId}
            categoryName={item.categoryName}
            subCategories={item.subCategories}
            selectedValues={subjectExpertise[item.categoryId] || []}
            onSelectionChange={handleSubjectSelection}
            disabled={disabled}
          />
        ))}
        
        {filteredSubjectCategories.length === 0 && subjectSearchQuery && (
          <p className={styles.noResults}>No results found</p>
        )}
      </div>

      {/* ================= MENTORSHIP ================= */}
      <div>
        <h2>
          <span className={styles.blue}>Mentorship</span>{" "}
          <span className={styles.orange}>Offered</span>
        </h2>
        
        <input
          type="text"
          placeholder="Search mentorship offered..."
          value={mentorshipSearchQuery}
          onChange={(e) => setMentorshipSearchQuery(e.target.value)}
          className={styles.searchInput}
          disabled={disabled}
        />

        {filteredMentorshipCategories.map((item) => (
          <AccordionCategory
            key={item.categoryId}
            categoryId={item.categoryId}
            categoryName={item.categoryName}
            subCategories={item.subCategories}
            selectedValues={mentorshipOffered[item.categoryId] || []}
            onSelectionChange={handleMentorshipSelection}
            isOrange
            disabled={disabled}
          />
        ))}
        
        {filteredMentorshipCategories.length === 0 && mentorshipSearchQuery && (
          <p className={styles.noResults}>No results found</p>
        )}
      </div>

      {/* ================= HIDDEN FIELDS ================= */}
      <input type="hidden" {...register("subjectMatterExpertise")} />
      <input type="hidden" {...register("mentorshipOffered")} />
    </div>
  );
};

export default CategorySubCategory;
